import { useState, useEffect, useRef } from "react";
import { Connection, PublicKey, Transaction, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { getAssociatedTokenAddress, createTransferInstruction, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import {  
  createAssociatedTokenAccountInstruction,
  ASSOCIATED_TOKEN_PROGRAM_ID 
} from '@solana/spl-token';
import { getArenaPrice, calculateArenaAmount } from '../../birdeyeApi';

import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

// Audio file paths
const AUDIO_PATHS = {
  BUTTON_CLICK: '/sounds/casino/button_click.mp3',
  WIN_SMALL: '/win.wav',
  WIN_MEDIUM: '/win.wav',
  WIN_BIG: '/win.wav',
  WIN_MEGA: '/win.wav',
  AMBIENT: '/sounds/casino/ambient_casino.mp3',
  ERROR: '/sounds/casino/error.mp3',
  COUNTDOWN: '/sounds/casino/countdown.mp3',
  DRUM_ROLL: '/sounds/casino/drum_roll.mp3',
  TICKET_PURCHASE: '/sounds/casino/coins_burst.mp3'
};

// ARENA Token Configuration
const ARENA_TOKEN_MINT = new PublicKey("HFqQ6xKTEyL2RSS7b8Ss5Sn4qgFqbL5b4rZmDhMhpkin");
const PROJECT_WALLET = new PublicKey("7SDrFeojsehbxuigUkJQVAoKDndh4LBuPVWsWwBd4JRm");
const ARENA_DECIMALS = 6;
const ALCHEMY_RPC_URL = 'https://solana-mainnet.g.alchemy.com/v2/pbGjxwq4DDspjqXb8DkO-Q4H-SzdtrLC';

// Simple Avatar component
const Avatar = ({ seed, size = 80, className = "" }) => {
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#6C5CE7'];
  const bgColor = colors[seed.charCodeAt(0) % colors.length];
  const initial = seed.charAt(0).toUpperCase();
  
  return (
    <div 
      className={`flex items-center justify-center font-bold text-white rounded-full ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: bgColor,
        fontSize: size * 0.4
      }}
    >
      {initial}
    </div>
  );
};

export default function ArenaRaffle() {
  const CURRENT_VERSION = "1.0.0";
  const TOTAL_NUMBERS = 50;
  const TICKET_PRICE_USD = 1.0; // $1.00 per number
  const WINNER_PERCENTAGE = 0.7; // Winner gets 70% of pot

  const [arenaPrice, setArenaPrice] = useState(0);
  const [ticketPriceArena, setTicketPriceArena] = useState(0);
  const [isPriceLoading, setIsPriceLoading] = useState(true);

  // Add these hooks
  const { connection } = useConnection();
  const { publicKey, connected, disconnect, sendTransaction } = useWallet();
  const { setVisible } = useWalletModal();

  // User state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState(0);

  // Wallet state
  const [arenaBalance, setArenaBalance] = useState(0);
  const [solBalance, setSolBalance] = useState(0);
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);

  // Raffle state
  const [currentRaffle, setCurrentRaffle] = useState(null);
  const [myNumbers, setMyNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [winningNumber, setWinningNumber] = useState(null);
  const [isWinner, setIsWinner] = useState(false);
  const [prizeAmount, setPrizeAmount] = useState(0);
  const [transactionStatus, setTransactionStatus] = useState("");

  // UI state
  const [showUserStats, setShowUserStats] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showCelebrationOverlay, setShowCelebrationOverlay] = useState(false);
  const [celebrationType, setCelebrationType] = useState('');
  const [celebrationData, setCelebrationData] = useState(null);
  const [raffleHistory, setRaffleHistory] = useState([]);

  // Refs
  const audioRefs = useRef({});
  const ambientAudioRef = useRef(null);

  // Icon components
  const TicketIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  );

  const ClockIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const TrophyIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );

  const UsersIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );

  const ChartBarIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const UserIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const SpeakerWaveIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    </svg>
  );

  const SpeakerXMarkIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );

  const WalletIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );

  const ArrowLeftIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );

  const XMarkIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const BanknotesIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );

  // Fetch ARENA price on mount and periodically
  useEffect(() => {
    const fetchArenaPrice = async () => {
      setIsPriceLoading(true);
      try {
        const price = await getArenaPrice();
        setArenaPrice(price);
        
        // Calculate ticket price in ARENA
        const arenaAmount = calculateArenaAmount(TICKET_PRICE_USD, price);
        setTicketPriceArena(arenaAmount);
      } catch (error) {
        console.error('Error fetching ARENA price:', error);
        // Fallback to default price
        setArenaPrice(0.001);
        setTicketPriceArena(200);
      } finally {
        setIsPriceLoading(false);
      }
    };

    // Fetch immediately
    fetchArenaPrice();

    // Refresh price every 30 seconds
    const interval = setInterval(fetchArenaPrice, 30000);

    return () => clearInterval(interval);
  }, []);

  // Close wallet dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showWalletDropdown && !event.target.closest('.relative')) {
        setShowWalletDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWalletDropdown]);

  // Audio setup
  useEffect(() => {
    Object.entries(AUDIO_PATHS).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      if (key === 'AMBIENT') {
        audio.loop = true;
        audio.volume = 0.3;
        ambientAudioRef.current = audio;
      } else {
        audio.volume = key.includes('WIN') ? 0.7 : 0.5;
        audioRefs.current[key] = audio;
      }
    });
    
    if (soundEnabled && ambientAudioRef.current) {
      ambientAudioRef.current.play().catch(e => console.log("Audio autoplay prevented:", e));
    }
    
    return () => {
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
        ambientAudioRef.current = null;
      }
      Object.values(audioRefs.current).forEach(audio => audio.pause());
      audioRefs.current = {};
    };
  }, []);

  // Sound toggle effect
  useEffect(() => {
    if (ambientAudioRef.current) {
      if (soundEnabled) {
        ambientAudioRef.current.play().catch(e => console.log("Audio play prevented:", e));
      } else {
        ambientAudioRef.current.pause();
      }
    }
  }, [soundEnabled]);

  // Play sound function
  const playSound = (soundKey) => {
    if (!soundEnabled) return;
    const audioElement = audioRefs.current[soundKey];
    if (!audioElement) return;
    const sound = audioElement.cloneNode();
    sound.play().catch(e => console.log("Audio play prevented:", e));
  };

  // Helper function to make API calls
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(endpoint, {
      ...options,
      headers
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'API call failed');
    }
    
    return response.json();
  };

  // Extract user ID from token
  const extractUserIdFromToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload).userId;
    } catch (e) {
      return localStorage.getItem('userId');
    }
  };

  // Update wallet balances
  const updateBalances = async () => {
    if (!publicKey) return;
    const address = publicKey.toString();
    try {
      const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
      const pubkey = new PublicKey(address);
      
      // Get SOL balance
      const balance = await connection.getBalance(pubkey);
      setSolBalance(balance / LAMPORTS_PER_SOL);
      
      // Get ARENA token balance
      try {
        const tokenAccountAddress = await getAssociatedTokenAddress(
          ARENA_TOKEN_MINT,
          pubkey
        );
        
        // Check if token account exists
        const tokenAccountInfo = await connection.getAccountInfo(tokenAccountAddress);
        
        if (tokenAccountInfo) {
          // Get the parsed token balance
          const tokenBalance = await connection.getTokenAccountBalance(tokenAccountAddress);
          
          if (tokenBalance && tokenBalance.value) {
            const uiAmount = parseFloat(tokenBalance.value.uiAmount || 0);
            setArenaBalance(uiAmount);
            console.log("ARENA balance updated:", uiAmount);
          } else {
            setArenaBalance(0);
            console.log("Token balance response invalid");
          }
        } else {
          setArenaBalance(0);
          console.log("No ARENA token account found for this wallet");
        }
      } catch (error) {
        console.error('Error fetching ARENA balance:', error);
        setArenaBalance(0);
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      setSolBalance(0);
      setArenaBalance(0);
    }
  };

  // Check for connected wallet on mount
  useEffect(() => {
    if (connected && publicKey) {
      updateBalances();
    }
  }, [connected, publicKey]);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          setError("Authentication required. Please log in.");
          setLoading(false);
          return;
        }
        
        const userId = extractUserIdFromToken(token);
        if (!userId) {
          setError("User ID not found. Please log in again.");
          setLoading(false);
          return;
        }
        
        const userData = await apiCall(`/api/users/${userId}`);
        setUser(userData);
        setBalance(userData.chips || 0);
      } catch (err) {
        console.error('Failed to load user data:', err);
        setError("Failed to load user data. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, []);

  // Fetch raffle data
  const fetchRaffleData = async () => {
    try {
      const data = await apiCall('/api/raffle/current');
      
      if (data.success) {
        if (data.raffleEnding) {
          // Raffle is ending, check for results
          setTimeout(checkForDraw, 2000);
        } else {
          setCurrentRaffle(data.raffle);
          setMyNumbers(data.myNumbers || []);
          
          // Calculate current prize
          if (data.raffle) {
            const currentPrize = Math.floor(data.raffle.soldNumbers.length * ticketPriceArena * WINNER_PERCENTAGE);
            setPrizeAmount(currentPrize);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch raffle data:', error);
    }
  };

  // Fetch raffle history
  const fetchRaffleHistory = async () => {
    try {
      const data = await apiCall('/api/raffle/history');
      if (data.success) {
        setRaffleHistory(data.raffles);
      }
    } catch (error) {
      console.error('Failed to fetch raffle history:', error);
    }
  };

  // Check for draw results
  const checkForDraw = async () => {
    try {
      const data = await apiCall('/api/raffle/check-draw');
      
      if (data.success && data.winningNumber !== undefined) {
        setWinningNumber(data.winningNumber);
        setIsWinner(myNumbers.includes(data.winningNumber));
        setPrizeAmount(data.prizeAmount);
        setShowResults(true);
        playSound(myNumbers.includes(data.winningNumber) ? 'WIN_MEGA' : 'BUTTON_CLICK');
      }
    } catch (error) {
      console.error('Failed to check draw:', error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (!loading) {
      fetchRaffleData();
      const interval = setInterval(fetchRaffleData, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [loading, ticketPriceArena]);

  // Timer countdown
  useEffect(() => {
    const timer = setInterval(() => {
      if (currentRaffle && currentRaffle.endTime) {
        const now = new Date();
        const end = new Date(currentRaffle.endTime);
        const diff = end - now;
        
        if (diff <= 0) {
          setTimeRemaining("Drawing soon...");
        } else {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setTimeRemaining(`${minutes}m ${seconds}s`);
        }
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [currentRaffle]);

  // Purchase a number
  const purchaseNumber = async () => {
    if (!selectedNumber || !connected || !publicKey) {
      handleError("Please connect wallet and select a number!");
      return;
    }

    if (isPriceLoading) {
      handleError("Please wait, fetching current price...");
      return;
    }
    
    try {
      setIsPurchasing(true);
      setTransactionStatus("Preparing transaction...");
      playSound('BUTTON_CLICK');

      const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
      const fromPubkey = publicKey;
      
      // Get token accounts
      const fromTokenAccount = await getAssociatedTokenAddress(
        ARENA_TOKEN_MINT,
        fromPubkey
      );
      
      const toTokenAccount = await getAssociatedTokenAddress(
        ARENA_TOKEN_MINT,
        PROJECT_WALLET
      );
      
      // Check if accounts exist
      const fromAccountInfo = await connection.getAccountInfo(fromTokenAccount);
      const toAccountInfo = await connection.getAccountInfo(toTokenAccount);
      
      if (!fromAccountInfo) {
        throw new Error("Your ARENA token account doesn't exist");
      }
      
      // Get actual balance
      const tokenBalance = await connection.getTokenAccountBalance(fromTokenAccount);
      
      if (parseFloat(tokenBalance.value.uiAmount) < ticketPriceArena) {
        throw new Error(`Insufficient balance. You have ${tokenBalance.value.uiAmount} ARENA but need ${ticketPriceArena}`);
      }
      
      // Create transaction
      const transaction = new Transaction();
      
      // If destination account doesn't exist, create it first
      if (!toAccountInfo) {
        console.log("Creating destination token account...");
        
        const createATAInstruction = createAssociatedTokenAccountInstruction(
          fromPubkey, // payer
          toTokenAccount, // ata
          PROJECT_WALLET, // owner
          ARENA_TOKEN_MINT // mint
        );
        
        transaction.add(createATAInstruction);
      }
      
      // Create transfer instruction with dynamic amount
      const transferInstruction = createTransferInstruction(
        fromTokenAccount,
        toTokenAccount,
        fromPubkey,
        ticketPriceArena * Math.pow(10, ARENA_DECIMALS) // Use dynamic price
      );
      
      transaction.add(transferInstruction);
      
      setTransactionStatus("Waiting for wallet approval...");
      
      // Get recent blockhash
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromPubkey;
      
      // Sign and send transaction
      const signature = await sendTransaction(transaction, connection);
      
      // Wait 2 seconds for transaction to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Check transaction status once
      const status = await connection.getSignatureStatus(signature);
      
      if (status && status.value) {
        if (status.value.err) {
          throw new Error('Transaction failed: ' + JSON.stringify(status.value.err));
        }
        console.log("Transaction confirmed:", signature);
      }
      
      setTransactionStatus("Recording purchase...");
      
      // Record purchase in backend
      const data = await apiCall('/api/raffle/purchase', {
        method: 'POST',
        body: JSON.stringify({
          number: selectedNumber,
          ticketPrice: ticketPriceArena,
          ticketPriceUSD: TICKET_PRICE_USD,
          arenaPrice: arenaPrice,
          walletAddress: publicKey.toString(),
          transactionSignature: signature
        })
      });
      
      if (data.success) {
        setMyNumbers([...myNumbers, selectedNumber]);
        setSelectedNumber(null);
        fetchRaffleData(); // Refresh data
        
        setCelebrationType('success');
        setCelebrationData({ message: `Number ${selectedNumber} purchased successfully!` });
        setShowCelebrationOverlay(true);
        
        playSound('TICKET_PURCHASE');
        
        setTimeout(() => {
          setShowCelebrationOverlay(false);
        }, 2000);
        
        await updateBalances();
      }
    } catch (error) {
      console.error('Purchase error:', error);
      handleError(error.message || 'Failed to purchase number');
      playSound('ERROR');
    } finally {
      setIsPurchasing(false);
      setTransactionStatus("");
    }
  };

  const handleError = (errorMessage) => {
    setCelebrationType('error');
    setCelebrationData({ message: errorMessage });
    setShowCelebrationOverlay(true);
    setTimeout(() => setShowCelebrationOverlay(false), 3000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 border-4 border-purple-500 rounded-full relative flex items-center justify-center mx-auto">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full animate-pulse"></div>
            <div className="absolute inset-0 border-t-4 border-purple-500 rounded-full animate-spin"></div>
          </div>
          <div className="mt-6 text-lg">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 font-mono animate-pulse">
              ARENA RAFFLE
            </span>
            <div className="mt-2 text-sm text-gray-400">Loading raffle experience...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono relative -mt-16 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-pink-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-orange-500/10 rounded-full filter blur-3xl"></div>
      </div>

      {/* Main content */}
      <div className="relative w-full max-w-lg mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => {
              playSound('BUTTON_CLICK');
              window.history.back();
            }}
            className="bg-black/30 backdrop-blur-sm p-2 rounded-xl border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-all"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>

          <div className="absolute left-1/2 transform -translate-x-1/2 bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse shadow-lg shadow-orange-600/30">
            NEW
          </div>
          
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setSoundEnabled(!soundEnabled);
              }}
              className="bg-black/30 backdrop-blur-sm p-2 rounded-xl border border-gray-800 text-pink-400 hover:text-pink-300 hover:border-gray-700 transition-all"
            >
              {soundEnabled ? (
                <SpeakerWaveIcon className="h-5 w-5" />
              ) : (
                <SpeakerXMarkIcon className="h-5 w-5" />
              )}
            </button>
            
            <div className="relative">
              <button
                onClick={() => {
                  if (connected) {
                    setShowWalletDropdown(!showWalletDropdown);
                  } else {
                    setVisible(true);
                  }
                }}
                className={`p-2 rounded-xl backdrop-blur-sm border transition-all ${
                  connected 
                    ? 'bg-green-900/20 border-green-700/50 hover:border-green-600' 
                    : 'bg-red-900/20 border-red-700/50 hover:border-red-600'
                }`}
              >
                <WalletIcon className={`h-5 w-5 ${connected ? 'text-green-400' : 'text-red-400'}`} />
              </button>

              {/* Dropdown for connected wallet */}
              {showWalletDropdown && connected && publicKey && (
                <div className="absolute right-0 mt-2 bg-gray-900 rounded-xl border border-gray-700 shadow-2xl overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-800">
                    <div className="text-xs text-gray-400">Connected</div>
                    <div className="text-sm font-medium text-white">
                      {publicKey.toString().slice(0, 4)}...{publicKey.toString().slice(-4)}
                    </div>
                    <div className="flex gap-3 mt-1 text-xs">
                      <span className="text-yellow-400">{arenaBalance.toFixed(2)} ARENA</span>
                      <span className="text-blue-400">{solBalance.toFixed(3)} SOL</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(publicKey.toString());
                      setShowWalletDropdown(false);
                      handleError('Wallet address copied!');
                      playSound('BUTTON_CLICK');
                    }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-gray-800 transition-colors flex items-center space-x-2"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Address</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      disconnect();
                      setShowWalletDropdown(false);
                    }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-gray-800 transition-colors flex items-center space-x-2 text-red-400"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Disconnect</span>
                  </button>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setShowUserStats(true);
              }}
              className="bg-black/30 backdrop-blur-sm p-2 rounded-xl border border-gray-800 text-pink-400 hover:text-pink-300 hover:border-gray-700 transition-all"
            >
              <UserIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Main Raffle Info Card */}
        {currentRaffle && (
          <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md border border-gray-700/50 rounded-2xl p-4 mb-6 shadow-xl relative">
            <div className="space-y-3">
              {/* Prize and Timer Row */}
              <div className="flex items-center justify-between">
                {/* Prize Display */}
                <div>
                  <div className="text-xs text-gray-400 uppercase">Prize Pool</div>
                  <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                    {prizeAmount.toLocaleString()} <span className="text-lg">ARENA</span>
                  </div>
                </div>
                
                {/* Timer */}
                <div className="text-right">
                  <div className="text-xs text-gray-400 uppercase">Time Left</div>
                  <div className="text-xs font-semibold text-purple-300 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {timeRemaining}
                  </div>
                </div>
              </div>
              
              {/* Bottom Info Row */}
              <div className="flex items-center justify-between text-xs">
                {/* Numbers Sold */}
                <div className="text-gray-400">
                  {currentRaffle.soldNumbers.length}/{TOTAL_NUMBERS} numbers sold
                </div>
                
                {/* My Numbers */}
                {myNumbers.length > 0 && (
                  <div className="text-purple-400 font-medium">
                    <TicketIcon className="h-3 w-3 inline mr-1" />
                    {myNumbers.length} {myNumbers.length === 1 ? 'number' : 'numbers'}
                  </div>
                )}
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(currentRaffle.soldNumbers.length / TOTAL_NUMBERS) * 100}%` }}
                ></div>
              </div>
              
              {/* Price Display */}
              {arenaPrice > 0 && (
                <>
                  <div className="text-center text-xs text-green-400 pt-1 border-t border-gray-700/50">
                    Number Price: {ticketPriceArena} ARENA (${(arenaPrice * ticketPriceArena).toFixed(2)})
                  </div>
                  <div className="text-center text-xs pt-1 flex items-center justify-center gap-1">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-gray-500">Live:</span>
                    <span className="text-yellow-400 font-medium">${arenaPrice.toFixed(8)}</span>
                    <span className="text-gray-500">per ARENA</span>
                  </div>
                </>
              )}
            </div>
            
            {/* Success/Error celebration overlay */}
            {showCelebrationOverlay && (celebrationType === 'success' || celebrationType === 'error') && (
              <div className="absolute inset-0 flex items-center justify-center z-50 rounded-2xl bg-black/80">
                {celebrationType === 'success' && celebrationData && (
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-xl shadow-lg border border-purple-400 text-center">
                    <div className="text-base font-bold flex items-center justify-center">
                      <TicketIcon className="h-5 w-5 mr-2" />
                      Success!
                    </div>
                    <div className="text-sm mt-1">{celebrationData.message}</div>
                  </div>
                )}
                
                {celebrationType === 'error' && celebrationData && (
                  <div className="bg-red-700 text-white py-3 px-4 rounded-xl shadow-lg border border-red-500 text-center">
                    <div className="text-base font-bold">ERROR</div>
                    <div className="text-sm mt-1">{celebrationData.message}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Number Selection Section */}
        <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden border border-gray-700/50 mb-6">
          <div className="bg-gradient-to-b from-gray-900/50 to-black/50 p-5">
            <h3 className="text-center text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-4">
              Choose Your Lucky Number
            </h3>
            
            {/* My Numbers Display */}
            {myNumbers.length > 0 && (
              <div className="mb-4 bg-gray-800/50 rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-2">My Numbers:</p>
                <div className="flex flex-wrap gap-2">
                  {myNumbers.map(num => (
                    <div 
                      key={num}
                      className="px-3 py-1 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-sm"
                    >
                      #{num}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Number Grid */}
            <div className="grid grid-cols-5 gap-2 mb-4">
              {Array.from({ length: TOTAL_NUMBERS }, (_, i) => i + 1).map(num => {
                const isSold = currentRaffle?.soldNumbers.includes(num);
                const isMine = myNumbers.includes(num);
                const isSelected = selectedNumber === num;
                
                return (
                  <button
                    key={num}
                    onClick={() => !isSold && !isMine && setSelectedNumber(num)}
                    disabled={isSold || isMine || isPurchasing}
                    className={`
                      aspect-square rounded-xl flex items-center justify-center font-bold text-sm
                      transition-all duration-200 transform hover:scale-105
                      ${isSold && !isMine ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : ''}
                      ${isMine ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white ring-2 ring-green-400' : ''}
                      ${isSelected && !isSold && !isMine ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white ring-2 ring-purple-400' : ''}
                      ${!isSold && !isMine && !isSelected ? 'bg-gray-800/80 hover:bg-purple-700 text-gray-300' : ''}
                    `}
                  >
                    {num}
                  </button>
                );
              })}
            </div>
            
            {/* Purchase Button */}
            {selectedNumber && (
              <button
                onClick={purchaseNumber}
                disabled={
                  !connected ||
                  arenaBalance < ticketPriceArena ||
                  isPurchasing ||
                  isPriceLoading
                }
                className={`text-sm w-full py-4 rounded-xl font-bold text-lg tracking-wider transition-all duration-300 ${
                  connected && arenaBalance >= ticketPriceArena && !isPurchasing && !isPriceLoading
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white shadow-xl transform hover:scale-[1.02]'
                    : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                } flex items-center justify-center`}
              >
                {isPurchasing ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {transactionStatus}
                  </>
                ) : isPriceLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading price...
                  </>
                ) : (
                  <>
                    <TicketIcon className="h-5 w-5 mr-2" />
                    Buy #{selectedNumber} • {ticketPriceArena} ARENA
                  </>
                )}
              </button>
            )}
            
            {!connected && (
              <p className="text-center text-yellow-400 text-xs mt-3">
                Connect wallet to purchase numbers
              </p>
            )}
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button 
            onClick={() => {
              playSound('BUTTON_CLICK');
              fetchRaffleHistory();
              setShowHistory(true);
            }}
            className="text-sm flex items-center justify-center py-3 px-4 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm hover:from-gray-700/50 hover:to-gray-800/50 text-cyan-300 hover:text-cyan-200 rounded-xl border border-gray-700/50 transition-all"
          >
            <ChartBarIcon className="h-5 w-5 mr-2" />
            Past Raffles
          </button>
          
          <button
            onClick={() => {
              playSound('BUTTON_CLICK');
              window.location.href = 'https://jup.ag/tokens/HFqQ6xKTEyL2RSS7b8Ss5Sn4qgFqbL5b4rZmDhMhpkin';
            }}
            className="text-sm flex items-center justify-center py-3 px-4 bg-gradient-to-br from-yellow-700/50 to-amber-800/50 backdrop-blur-sm hover:from-yellow-600/50 hover:to-amber-700/50 text-yellow-300 hover:text-yellow-200 rounded-xl border border-yellow-700/50 transition-all"
          >
            <BanknotesIcon className="h-5 w-5 mr-2" />
            Buy $ARENA
          </button>
        </div>

        {/* Results Modal */}
        {showResults && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700">
              <h2 className="text-2xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 mb-6">
                Raffle Results!
              </h2>
              
              <div className="mb-6">
                <p className="text-center text-gray-400 mb-4">Winning Number</p>
                <div className="text-6xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  #{winningNumber}
                </div>
              </div>
              
              {isWinner ? (
                <div className="mb-6 text-center">
                  <p className="text-2xl font-bold text-green-400 mb-2">
                    🎉 CONGRATULATIONS! 🎉
                  </p>
                  <p className="text-xl">You won {prizeAmount} ARENA!</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Prize will be sent to your wallet
                  </p>
                </div>
              ) : (
                <div className="mb-6 text-center">
                  <p className="text-xl text-gray-400">Better luck next time!</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Winner gets {prizeAmount} ARENA
                  </p>
                </div>
              )}
              
              <button
                onClick={() => {
                  setShowResults(false);
                  setWinningNumber(null);
                  setIsWinner(false);
                  fetchRaffleData();
                }}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold rounded-xl transition-all"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistory && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 overflow-auto">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-2xl p-6 w-full max-w-sm border border-gray-700 max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">Past Raffles</h2>
                <button 
                  onClick={() => {
                    playSound('BUTTON_CLICK');
                    setShowHistory(false);
                  }}
                  className="text-gray-300 hover:text-white p-2"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              {raffleHistory.length === 0 ? (
                <div className="text-center text-gray-400 py-12">
                  <TrophyIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No past raffles yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {raffleHistory.map((raffle, index) => (
                    <div key={raffle._id || index} className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-400">{new Date(raffle.drawnAt).toLocaleString()}</span>
                        <span className="text-xs text-yellow-400">{raffle.prizeAmount} ARENA</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm text-gray-500">Winner:</span>
                          <div className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                            #{raffle.winningNumber}
                          </div>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          {raffle.soldNumbers.length}/{TOTAL_NUMBERS} sold
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Stats Modal */}
        {showUserStats && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 overflow-auto">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-2xl p-6 w-full max-w-sm border border-gray-700 max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">Player Stats</h2>
                <button 
                  onClick={() => {
                    playSound('BUTTON_CLICK');
                    setShowUserStats(false);
                  }}
                  className="text-gray-300 hover:text-white p-2"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="flex flex-col items-center mb-6">
                <Avatar
                  seed={user?.referralCode || user?.emailPrefix || 'guest'}
                  size={80}
                  className="mb-3"
                />
                <div className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  {user?.emailPrefix || 'Guest'}
                </div>
                <div className="text-sm text-gray-300 mt-1">
                  {formatCurrency(user?.chips || 0)} Chips
                </div>
                {connected && publicKey && (
                  <div className="text-xs text-gray-400 mt-2">
                    {publicKey.toString().slice(0, 6)}...{publicKey.toString().slice(-4)}
                  </div>
                )}
              </div>
              
              <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 rounded-xl shadow-lg mb-4 border border-gray-700">
                <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 font-semibold mb-3">
                  RAFFLE STATS
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Numbers:</span>
                    <span className="text-blue-300 font-mono">{user?.totalRaffleTickets || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Wins:</span>
                    <span className="text-green-300 font-mono">{user?.raffleWins || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Biggest Win:</span>
                    <span className="text-pink-300 font-mono">{user?.largestRaffleWin || 0} ARENA</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Won:</span>
                    <span className="text-purple-300 font-mono">{user?.totalRaffleWinnings || 0} ARENA</span>
                  </div>
                </div>
              </div>
              
              {connected && publicKey && (
                <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 rounded-xl shadow-lg border border-gray-700">
                  <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 font-semibold mb-3">
                    WALLET INFO
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">ARENA Balance:</span>
                      <span className="text-yellow-300 font-mono">{arenaBalance.toFixed(2)} ARENA</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">SOL Balance:</span>
                      <span className="text-blue-300 font-mono">{solBalance.toFixed(4)} SOL</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
